import {z} from "zod/v4-mini"
import "zod/v4"
import {discriminatedCyclesUnion} from "#material/cycles/discriminated-cycles-union"
import type {
    AddClosureNode,
    AmbientOcclusionNode,
    BrickTextureNode,
    BrightnessContrastNode,
    BumpNode,
    ClosureInput,
    ClosureOutput,
    ColorInput,
    ColorNode,
    ColorOutput,
    CombineHsvNode,
    CombineRgbNode,
    CombineXyzNode,
    CyclesBlendMode,
    DiffuseBsdfNode,
    DisplacementNode,
    EmissionNode,
    EnvironmentTextureNode,
    FloatInput,
    FloatOutput,
    FresnelNode,
    GammaNode,
    GeometryNode,
    GlassBsdfNode,
    GlossyBsdfNode,
    GradientTextureNode,
    HsvNode,
    ImageTextureNode,
    InvertNode,
    LayerWeightNode,
    LightPathNode,
    MapRangeNode,
    MappingNode,
    MaterialOutput,
    MaterialOutputNode,
    MathNode,
    MixClosureNode,
    MixColorNode,
    MixFloatNode,
    MixNode,
    MixVectorNode,
    MixVectorNonUniformNode,
    NoiseTextureNode,
    NormalInput,
    NormalMapNode,
    NormalNode,
    NormalOutput,
    OutputNode,
    PointInput,
    PointOutput,
    PrincipledBsdfNode,
    RgbCurvesNode,
    RgbRampNode,
    RgbToBwNode,
    SeparateHsvNode,
    SeparateRgbNode,
    SeparateXyzNode,
    SubsurfaceScatteringNode,
    TangentNode,
    TextureCoordinateNode,
    TranslucentBsdfNode,
    TransparentBsdfNode,
    UvMapNode,
    ValueNode,
    VectorInput,
    VectorMathNode,
    VectorOutput,
    VoronoiTextureNode,
} from "#material/cycles/types"
import {imageSchema} from "#material/nodes/base/material/validation/shader-node"

const cyclesBlendModeSchema = z.enum([
    "mix",
    "add",
    "multiply",
    "screen",
    "overlay",
    "subtract",
    "divide",
    "difference",
    "darken",
    "lighten",
    "dodge",
    "burn",
    "hue",
    "saturation",
    "value",
    "color",
    "soft_light",
    "linear_light",
    "exclusion",
]) satisfies z.ZodMiniType<CyclesBlendMode>

export const materialOutputSchema = discriminatedCyclesUnion<MaterialOutput>(() => [[materialOutputNodeSchema, "surface"]], "MaterialOutput")
export const closureOutputSchema = discriminatedCyclesUnion<ClosureOutput>(
    () => [
        [addClosureNodeSchema, "closure"],
        [diffuseBsdfNodeSchema, "bsdf"],
        [glossyBsdfNodeSchema, "bsdf"],
        [emissionNodeSchema, "emission"],
        [mixClosureNodeSchema, "closure"],
        [principledBsdfNodeSchema, "bsdf"],
        [translucentBsdfNodeSchema, "bsdf"],
        [transparentBsdfNodeSchema, "bsdf"],
        [glassBsdfNodeSchema, "bsdf"],
        [subsurfaceScatteringNodeSchema, "bssrdf"],
    ],
    "ClosureOutput",
)
export const closureInputSchema: z.ZodMiniType<ClosureInput> = closureOutputSchema
export const colorOutputSchema = discriminatedCyclesUnion<ColorOutput>(
    () => [
        [mixColorNodeSchema, "result"],
        [ambientOcclusionNodeSchema, "color"],
        [brickTextureNodeSchema, "color"],
        [brightnessContrastNodeSchema, "color"],
        [colorNodeSchema, "color"],
        [combineHsvNodeSchema, "color"],
        [combineRgbNodeSchema, "image"],
        [gammaNodeSchema, "color"],
        [gradientTextureSchema, "color"],
        [hsvNodeSchema, "color"],
        [imageTextureNodeSchema, "color"],
        [invertNodeSchema, "color"],
        [mixNodeSchema, "color"],
        [noiseTextureNodeSchema, "color"],
        [rgbRampNodeSchema, "color"],
        [rgbCurvesNodeSchema, "value"],
        [rgbToBwNodeSchema, "val"],
        [voronoiTextureNodeSchema, "color"],
    ],
    "ColorOutput",
)
export const floatOutputSchema = discriminatedCyclesUnion<FloatOutput>(
    () => [
        [mixFloatNodeSchema, "result"],
        [normalNodeSchema, "dot"],
        [ambientOcclusionNodeSchema, "ao"],
        [brickTextureNodeSchema, "fac"],
        [fresnelNodeSchema, "fac"],
        [geometrySchema, "backfacing"],
        [geometrySchema, "pointiness"],
        [geometrySchema, "random_per_island"],
        [gradientTextureSchema, "fac"],
        [imageTextureNodeSchema, "alpha"],
        [layerWeightNodeSchema, "facing"],
        [layerWeightNodeSchema, "fresnel"],
        [lightPathNodeSchema, "is_camera_ray"],
        [lightPathNodeSchema, "is_shadow_ray"],
        [lightPathNodeSchema, "is_diffuse_ray"],
        [lightPathNodeSchema, "is_glossy_ray"],
        [lightPathNodeSchema, "is_singular_ray"],
        [lightPathNodeSchema, "is_reflection_ray"],
        [lightPathNodeSchema, "is_transmission_ray"],
        [lightPathNodeSchema, "is_volume_scatter_ray"],
        [lightPathNodeSchema, "ray_length"],
        [lightPathNodeSchema, "ray_depth"],
        [lightPathNodeSchema, "diffuse_depth"],
        [lightPathNodeSchema, "glossy_depth"],
        [lightPathNodeSchema, "transparent_depth"],
        [lightPathNodeSchema, "transmission_depth"],
        [noiseTextureNodeSchema, "fac"],
        [mapRangeNodeSchema, "result"],
        [mathNodeSchema, "value"],
        [rgbRampNodeSchema, "alpha"],
        [rgbToBwNodeSchema, "val"],
        [separateHsvNodeSchema, "h"],
        [separateHsvNodeSchema, "s"],
        [separateHsvNodeSchema, "v"],
        [separateRgbNodeSchema, "r"],
        [separateRgbNodeSchema, "g"],
        [separateRgbNodeSchema, "b"],
        [separateXyzNodeSchema, "x"],
        [separateXyzNodeSchema, "y"],
        [separateXyzNodeSchema, "z"],
        [valueNodeSchema, "value"],
        [vectorMathNodeSchema, "value"],
        [voronoiTextureNodeSchema, "distance"],
        [voronoiTextureNodeSchema, "radius"],
        [voronoiTextureNodeSchema, "w"],
    ],
    "FloatOutput",
)
export const normalOutputSchema = discriminatedCyclesUnion<NormalOutput>(
    () => [
        [normalNodeSchema, "normal"],
        [bumpNodeSchema, "normal"],
        [geometrySchema, "normal"],
        [geometrySchema, "tangent"],
        [geometrySchema, "true_normal"],
        [tangentNodeSchema, "tangent"],
        [normalMapNodeSchema, "normal"],
        [textureCoordinateNodeSchema, "normal"],
        [textureCoordinateNodeSchema, "reflection"],
    ],
    "NormalOutput",
)
export const pointOutputSchema = discriminatedCyclesUnion<PointOutput>(
    () => [
        [geometrySchema, "position"],
        [geometrySchema, "parametric"],
        [mappingNodeSchema, "vector"],
        [textureCoordinateNodeSchema, "uv"],
        [textureCoordinateNodeSchema, "object"],
        [textureCoordinateNodeSchema, "camera"],
        [textureCoordinateNodeSchema, "window"],
        [textureCoordinateNodeSchema, "generated"],
        [textureCoordinateNodeSchema, "reflection"],
        [textureCoordinateNodeSchema, "normal"],
        [uvMapNodeSchema, "uv"],
        [voronoiTextureNodeSchema, "position"],
    ],
    "PointOutput",
)
export const vectorOutputSchema = discriminatedCyclesUnion<VectorOutput>(
    () => [
        [mixVectorNodeSchema, "result"],
        [combineXyzNodeSchema, "vector"],
        [geometrySchema, "incoming"],
        [vectorMathNodeSchema, "vector"],
        [displacementNodeSchema, "displacement"],
    ],
    "VectorOutput",
)
export const colorInputSchema: z.ZodMiniType<ColorInput> = z.union([
    colorOutputSchema,
    floatOutputSchema,
    pointOutputSchema,
    vectorOutputSchema,
    normalOutputSchema,
])
export const vectorInputSchema: z.ZodMiniType<VectorInput> = z.union([
    colorOutputSchema,
    floatOutputSchema,
    pointOutputSchema,
    vectorOutputSchema,
    normalOutputSchema,
])
export const floatInputSchema: z.ZodMiniType<FloatInput> = z.union([
    colorOutputSchema,
    floatOutputSchema,
    pointOutputSchema,
    vectorOutputSchema,
    normalOutputSchema,
])
export const normalInputSchema: z.ZodMiniType<NormalInput> = z.union([
    colorOutputSchema,
    floatOutputSchema,
    pointOutputSchema,
    vectorOutputSchema,
    normalOutputSchema,
])
export const pointInputSchema: z.ZodMiniType<PointInput> = z.union([
    colorOutputSchema,
    floatOutputSchema,
    pointOutputSchema,
    vectorOutputSchema,
    normalOutputSchema,
])
// TODO
const transformSchema = z.never()

const orUndefined = <T extends z.ZodMiniType>(schema: T) => z.union([schema, z.undefined()])

export const addClosureNodeSchema = z.strictObject({
    type: z.literal("add_closure"),
    inputs: z.strictObject({
        closure1: orUndefined(closureInputSchema),
        closure2: orUndefined(closureInputSchema),
    }),
    parameters: z.strictObject({}),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<AddClosureNode>

export const ambientOcclusionNodeSchema = z.strictObject({
    type: z.literal("ambient_occlusion"),
    inputs: z.strictObject({
        color: orUndefined(colorInputSchema),
        distance: orUndefined(floatInputSchema),
        normal: orUndefined(normalInputSchema),
    }),
    parameters: z.strictObject({
        inside: orUndefined(z.boolean()),
        only_local: orUndefined(z.boolean()),
        samples: orUndefined(z.number()),
        color: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        distance: orUndefined(z.number()),
        normal: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<AmbientOcclusionNode>

export const brickTextureNodeSchema = z.strictObject({
    type: z.literal("brick_texture"),
    inputs: z.strictObject({
        bias: orUndefined(floatInputSchema),
        brick_width: orUndefined(floatInputSchema),
        color1: orUndefined(colorInputSchema),
        color2: orUndefined(colorInputSchema),
        mortar: orUndefined(colorInputSchema),
        mortar_size: orUndefined(floatInputSchema),
        mortar_smooth: orUndefined(floatInputSchema),
        row_height: orUndefined(floatInputSchema),
        scale: orUndefined(floatInputSchema),
        vector: orUndefined(pointInputSchema),
    }),
    parameters: z.strictObject({
        bias: orUndefined(z.number()),
        brick_width: orUndefined(z.number()),
        color1: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        color2: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        mortar: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        mortar_size: orUndefined(z.number()),
        mortar_smooth: orUndefined(z.number()),
        offset: orUndefined(z.number()),
        offset_frequency: orUndefined(z.number()),
        row_height: orUndefined(z.number()),
        scale: orUndefined(z.number()),
        squash: orUndefined(z.number()),
        squash_frequency: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<BrickTextureNode>

export const brightnessContrastNodeSchema = z.strictObject({
    type: z.literal("brightness_contrast"),
    inputs: z.strictObject({
        bright: orUndefined(floatInputSchema),
        contrast: orUndefined(floatInputSchema),
        color: orUndefined(colorInputSchema),
    }),
    parameters: z.strictObject({
        bright: orUndefined(z.number()),
        contrast: orUndefined(z.number()),
        color: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<BrightnessContrastNode>

export const bumpNodeSchema = z.strictObject({
    type: z.literal("bump"),
    inputs: z.strictObject({
        distance: orUndefined(floatInputSchema),
        height: orUndefined(floatInputSchema),
        normal: orUndefined(normalInputSchema),
        sample_center: orUndefined(floatInputSchema),
        sample_x: orUndefined(floatInputSchema),
        sample_y: orUndefined(floatInputSchema),
        strength: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        distance: orUndefined(z.number()),
        height: orUndefined(z.number()),
        invert: orUndefined(z.boolean()),
        normal: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        use_object_space: orUndefined(z.boolean()),
        sample_center: orUndefined(z.number()),
        sample_x: orUndefined(z.number()),
        sample_y: orUndefined(z.number()),
        strength: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<BumpNode>

export const colorNodeSchema = z.strictObject({
    type: z.literal("color"),
    inputs: z.strictObject({}),
    parameters: z.strictObject({
        value: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<ColorNode>

export const combineHsvNodeSchema = z.strictObject({
    type: z.literal("combine_hsv"),
    inputs: z.strictObject({
        h: orUndefined(floatInputSchema),
        s: orUndefined(floatInputSchema),
        v: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        h: orUndefined(z.number()),
        s: orUndefined(z.number()),
        v: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<CombineHsvNode>

export const combineRgbNodeSchema = z.strictObject({
    type: z.literal("combine_rgb"),
    inputs: z.strictObject({
        r: orUndefined(floatInputSchema),
        g: orUndefined(floatInputSchema),
        b: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        r: orUndefined(z.number()),
        g: orUndefined(z.number()),
        b: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<CombineRgbNode>

export const combineXyzNodeSchema = z.strictObject({
    type: z.literal("combine_xyz"),
    inputs: z.strictObject({
        x: orUndefined(floatInputSchema),
        y: orUndefined(floatInputSchema),
        z: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        x: orUndefined(z.number()),
        y: orUndefined(z.number()),
        z: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<CombineXyzNode>

export const diffuseBsdfNodeSchema = z.strictObject({
    type: z.literal("diffuse_bsdf"),
    inputs: z.strictObject({
        color: orUndefined(colorInputSchema),
        normal: orUndefined(normalInputSchema),
        roughness: orUndefined(floatInputSchema),
        surface_mix_weight: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        color: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        normal: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        roughness: orUndefined(z.number()),
        surface_mix_weight: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<DiffuseBsdfNode>

export const glassBsdfNodeSchema = z.strictObject({
    type: z.literal("glass_bsdf"),
    inputs: z.strictObject({
        color: orUndefined(colorInputSchema),
        normal: orUndefined(normalInputSchema),
        roughness: orUndefined(floatInputSchema),
        IOR: orUndefined(floatInputSchema),
        surface_mix_weight: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        color: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        normal: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        roughness: orUndefined(z.number()),
        IOR: orUndefined(z.number()),
        surface_mix_weight: orUndefined(z.number()),
        distribution: orUndefined(z.enum(["beckmann", "ggx", "multi_ggx"])),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<GlassBsdfNode>

export const glossyBsdfNodeSchema = z.strictObject({
    type: z.literal("glossy_bsdf"),
    inputs: z.strictObject({
        color: orUndefined(colorInputSchema),
        normal: orUndefined(normalInputSchema),
        roughness: orUndefined(floatInputSchema),
        anisotropy: orUndefined(floatInputSchema),
        rotation: orUndefined(floatInputSchema),
        tangent: orUndefined(normalInputSchema),
        surface_mix_weight: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        color: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        normal: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        roughness: orUndefined(z.number()),
        anisotropy: orUndefined(z.number()),
        rotation: orUndefined(z.number()),
        tangent: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        surface_mix_weight: orUndefined(z.number()),
        distribution: orUndefined(z.enum(["beckmann", "ggx", "ashikhmin_shirley", "multi_ggx"])),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<GlossyBsdfNode>

export const displacementNodeSchema = z.strictObject({
    type: z.literal("displacement"),
    inputs: z.strictObject({
        height: orUndefined(floatInputSchema),
        midlevel: orUndefined(floatInputSchema),
        normal: orUndefined(normalInputSchema),
        scale: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        height: orUndefined(z.number()),
        midlevel: orUndefined(z.number()),
        normal: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        scale: orUndefined(z.number()),
        space: orUndefined(z.enum(["world", "object"])),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<DisplacementNode>

export const emissionNodeSchema = z.strictObject({
    type: z.literal("emission"),
    inputs: z.strictObject({
        color: orUndefined(colorInputSchema),
        strength: orUndefined(floatInputSchema),
        surface_mix_weight: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        color: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        strength: orUndefined(z.number()),
        surface_mix_weight: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<EmissionNode>

export const fresnelNodeSchema = z.strictObject({
    type: z.literal("fresnel"),
    inputs: z.strictObject({
        normal: orUndefined(normalInputSchema),
        IOR: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        normal: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        IOR: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<FresnelNode>

export const gammaNodeSchema = z.strictObject({
    type: z.literal("gamma"),
    inputs: z.strictObject({
        gamma: orUndefined(floatInputSchema),
        color: orUndefined(colorInputSchema),
    }),
    parameters: z.strictObject({
        gamma: orUndefined(z.number()),
        color: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<GammaNode>

export const geometrySchema = z.strictObject({
    type: z.literal("geometry"),
    inputs: z.strictObject({}),
    parameters: z.strictObject({}),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<GeometryNode>

export const gradientTextureSchema = z.strictObject({
    type: z.literal("gradient_texture"),
    inputs: z.strictObject({
        vector: orUndefined(pointInputSchema),
    }),
    parameters: z.strictObject({
        gradient_type: orUndefined(z.enum(["linear", "quadratic", "easing", "diagonal", "radial", "quadratic_sphere", "spherical"])),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<GradientTextureNode>

export const hsvNodeSchema = z.strictObject({
    type: z.literal("hsv"),
    inputs: z.strictObject({
        fac: orUndefined(floatInputSchema),
        color: orUndefined(colorInputSchema),
        hue: orUndefined(floatInputSchema),
        saturation: orUndefined(floatInputSchema),
        value: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        fac: orUndefined(z.number()),
        color: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        hue: orUndefined(z.number()),
        saturation: orUndefined(z.number()),
        value: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<HsvNode>

export const imageTextureNodeSchema = z.strictObject({
    type: z.literal("image_texture"),
    inputs: z.strictObject({
        vector: orUndefined(pointInputSchema),
    }),
    parameters: z.strictObject({
        alpha_type: orUndefined(z.enum(["auto", "unassociated", "associated", "channel_packed", "ignore"])),
        animated: orUndefined(z.boolean()),
        colorspace: orUndefined(z.string()),
        extension: orUndefined(z.enum(["periodic", "clamp", "black", "mirror"])),
        filename: orUndefined(z.string()),
        interpolation: orUndefined(z.enum(["closest", "linear", "cubic", "smart"])),
        projection: orUndefined(z.enum(["flat", "box", "sphere", "tube"])),
        projection_blend: orUndefined(z.number()),
        tiles: orUndefined(z.array(z.number())),
    }),
    resources: z.strictObject({
        image: imageSchema,
    }),
}) satisfies z.ZodMiniType<ImageTextureNode>

export const invertNodeSchema = z.strictObject({
    type: z.literal("invert"),
    inputs: z.strictObject({
        fac: orUndefined(floatInputSchema),
        color: orUndefined(colorInputSchema),
    }),
    parameters: z.strictObject({
        fac: orUndefined(z.number()),
        color: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<InvertNode>

export const layerWeightNodeSchema = z.strictObject({
    type: z.literal("layer_weight"),
    inputs: z.strictObject({
        blend: orUndefined(floatInputSchema),
        normal: orUndefined(normalInputSchema),
    }),
    parameters: z.strictObject({
        blend: orUndefined(z.number()),
        normal: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<LayerWeightNode>

export const lightPathNodeSchema = z.strictObject({
    type: z.literal("light_path"),
    inputs: z.strictObject({}),
    parameters: z.strictObject({}),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<LightPathNode>

export const mappingNodeSchema = z.strictObject({
    type: z.literal("mapping"),
    inputs: z.strictObject({
        vector: orUndefined(pointInputSchema),
        location: orUndefined(pointInputSchema),
        rotation: orUndefined(pointInputSchema),
        scale: orUndefined(pointInputSchema),
    }),
    parameters: z.strictObject({
        location: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        mapping_type: orUndefined(z.enum(["point", "normal", "texture", "vector"])),
        rotation: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        scale: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        vector: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<MappingNode>

export const mapRangeNodeSchema = z.strictObject({
    type: z.literal("map_range"),
    inputs: z.strictObject({
        from_max: orUndefined(floatInputSchema),
        from_min: orUndefined(floatInputSchema),
        to_max: orUndefined(floatInputSchema),
        to_min: orUndefined(floatInputSchema),
        steps: orUndefined(floatInputSchema),
        value: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        clamp: orUndefined(z.boolean()),
        from_max: orUndefined(z.number()),
        from_min: orUndefined(z.number()),
        range_type: orUndefined(z.enum(["linear", "stepped", "smoothstep", "smootherstep"])),
        steps: orUndefined(z.number()),
        to_max: orUndefined(z.number()),
        to_min: orUndefined(z.number()),
        value: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<MapRangeNode>

export const materialOutputNodeSchema = z.strictObject({
    type: z.literal("material_output"),
    inputs: z.strictObject({
        displacement: orUndefined(vectorInputSchema),
        surface: orUndefined(closureInputSchema),
        volume: orUndefined(closureInputSchema),
    }),
    parameters: z.strictObject({
        displacement: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        name: orUndefined(z.string()),
        use_mis: orUndefined(z.boolean()),
        shadow_catcher: orUndefined(z.boolean()),
    }),
    resources: orUndefined(z.strictObject({})),
}) satisfies z.ZodMiniType<MaterialOutputNode>

export const mathNodeSchema = z.strictObject({
    type: z.literal("math"),
    inputs: z.strictObject({
        value1: orUndefined(floatInputSchema),
        value2: orUndefined(floatInputSchema),
        value3: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        math_type: orUndefined(
            z.enum([
                "add",
                "subtract",
                "multiply",
                "divide",
                "multiply_add",
                "sine",
                "cosine",
                "tangent",
                "sinh",
                "cosh",
                "tanh",
                "arcsine",
                "arccosine",
                "arctangent",
                "power",
                "logarithm",
                "minimum",
                "maximum",
                "round",
                "less_than",
                "greater_than",
                "modulo",
                "floored_modulo",
                "absolute",
                "arctan2",
                "floor",
                "ceil",
                "fraction",
                "trunc",
                "snap",
                "wrap",
                "pingpong",
                "sqrt",
                "inversesqrt",
                "sign",
                "exponent",
                "radians",
                "degrees",
                "smoothmin",
                "smoothmax",
                "compare",
            ]),
        ),
        use_clamp: orUndefined(z.boolean()),
        value1: orUndefined(z.number()),
        value2: orUndefined(z.number()),
        value3: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<MathNode>

export const mixClosureNodeSchema = z.strictObject({
    type: z.literal("mix_closure"),
    inputs: z.strictObject({
        fac: orUndefined(floatInputSchema),
        closure1: orUndefined(closureInputSchema),
        closure2: orUndefined(closureInputSchema),
    }),
    parameters: z.strictObject({
        fac: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<MixClosureNode>

export const mixNodeSchema = z.strictObject({
    type: z.literal("mix"),
    inputs: z.strictObject({
        color1: orUndefined(colorInputSchema),
        color2: orUndefined(colorInputSchema),
        fac: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        mix_type: orUndefined(cyclesBlendModeSchema),
        color1: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        color2: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        fac: orUndefined(z.number()),
        use_clamp: orUndefined(z.boolean()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<MixNode>

export const mixColorNodeSchema = z.strictObject({
    type: z.literal("mix_color"),
    inputs: z.strictObject({
        a: orUndefined(colorInputSchema),
        b: orUndefined(colorInputSchema),
        fac: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        blend_type: z.optional(cyclesBlendModeSchema),
        a: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        b: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        fac: orUndefined(z.number()),
        use_clamp: orUndefined(z.boolean()),
        use_clamp_result: orUndefined(z.boolean()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<MixColorNode>

export const mixFloatNodeSchema = z.strictObject({
    type: z.literal("mix_float"),
    inputs: z.strictObject({
        a: orUndefined(floatInputSchema),
        b: orUndefined(floatInputSchema),
        fac: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        a: orUndefined(z.number()),
        b: orUndefined(z.number()),
        fac: orUndefined(z.number()),
        use_clamp: orUndefined(z.boolean()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<MixFloatNode>

export const mixVectorNonUniformNodeSchema = z.strictObject({
    type: z.literal("mix_vector_non_uniform"),
    inputs: z.strictObject({
        a: orUndefined(vectorInputSchema),
        b: orUndefined(vectorInputSchema),
        fac: orUndefined(vectorInputSchema),
    }),
    parameters: z.strictObject({
        a: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        b: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        fac: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        use_clamp: orUndefined(z.boolean()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<MixVectorNonUniformNode>

export const mixVectorNodeSchema = z.strictObject({
    type: z.literal("mix_vector"),
    inputs: z.strictObject({
        a: orUndefined(vectorInputSchema),
        b: orUndefined(vectorInputSchema),
        fac: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        a: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        b: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        fac: orUndefined(z.number()),
        use_clamp: orUndefined(z.boolean()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<MixVectorNode>

export const noiseTextureNodeSchema = z.strictObject({
    type: z.literal("noise_texture"),
    inputs: z.strictObject({
        detail: orUndefined(floatInputSchema),
        distortion: orUndefined(floatInputSchema),
        gain: orUndefined(floatInputSchema),
        lacunarity: orUndefined(floatInputSchema),
        offset: orUndefined(floatInputSchema),
        roughness: orUndefined(floatInputSchema),
        scale: orUndefined(floatInputSchema),
        vector: orUndefined(pointInputSchema),
        w: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        detail: orUndefined(z.number()),
        dimensions: orUndefined(z.enum(["1D", "2D", "3D", "4D"])),
        distortion: orUndefined(z.number()),
        gain: orUndefined(z.number()),
        lacunarity: orUndefined(z.number()),
        offset: orUndefined(z.number()),
        roughness: orUndefined(z.number()),
        scale: orUndefined(z.number()),
        type: orUndefined(z.enum(["multifractal", "fBM", "ridged_multifractal", "hybrid_multifractal", "hetero_terrain"])),
        use_normalize: orUndefined(z.boolean()),
        vector: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        w: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<NoiseTextureNode>

export const normalMapNodeSchema = z.strictObject({
    type: z.literal("normal_map"),
    inputs: z.strictObject({
        color: orUndefined(colorInputSchema),
        strength: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        attribute: orUndefined(z.string()),
        color: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        space: orUndefined(z.enum(["tangent", "object", "world", "blender_object", "blender_world"])),
        strength: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<NormalMapNode>

export const outputNodeSchema = z.strictObject({
    type: z.literal("output"),
    inputs: z.strictObject({
        displacement: orUndefined(vectorInputSchema),
        normal: orUndefined(normalInputSchema),
        surface: orUndefined(closureInputSchema),
        volume: orUndefined(closureInputSchema),
    }),
    parameters: z.strictObject({}),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<OutputNode>

export const principledBsdfNodeSchema = z.strictObject({
    type: z.literal("principled_bsdf"),
    inputs: z.strictObject({
        alpha: orUndefined(floatInputSchema),
        anisotropic: orUndefined(floatInputSchema),
        anisotropic_rotation: orUndefined(floatInputSchema),
        base_color: orUndefined(colorInputSchema),
        coat_ior: orUndefined(floatInputSchema),
        coat_normal: orUndefined(normalInputSchema),
        coat_tint: orUndefined(colorInputSchema),
        coat_weight: orUndefined(floatInputSchema),
        coat_roughness: orUndefined(floatInputSchema),
        emission_color: orUndefined(colorInputSchema),
        emission_strength: orUndefined(floatInputSchema),
        ior: orUndefined(floatInputSchema),
        metallic: orUndefined(floatInputSchema),
        normal: orUndefined(normalInputSchema),
        roughness: orUndefined(floatInputSchema),
        sheen_roughness: orUndefined(floatInputSchema),
        sheen_tint: orUndefined(colorInputSchema),
        sheen_weight: orUndefined(floatInputSchema),
        specular_ior_level: orUndefined(floatInputSchema),
        specular_tint: orUndefined(colorInputSchema),
        subsurface_anisotropy: orUndefined(floatInputSchema),
        subsurface_ior: orUndefined(floatInputSchema),
        subsurface_radius: orUndefined(vectorInputSchema),
        subsurface_scale: orUndefined(floatInputSchema),
        subsurface_weight: orUndefined(floatInputSchema),
        surface_mix_weight: orUndefined(floatInputSchema),
        tangent: orUndefined(normalInputSchema),
        thin_film_ior: orUndefined(floatInputSchema),
        thin_film_thickness: orUndefined(floatInputSchema),
        transmission_weight: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        alpha: orUndefined(z.number()),
        anisotropic: orUndefined(z.number()),
        anisotropic_rotation: orUndefined(z.number()),
        base_color: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        coat_ior: orUndefined(z.number()),
        coat_normal: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        coat_tint: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        coat_weight: orUndefined(z.number()),
        coat_roughness: orUndefined(z.number()),
        distribution: orUndefined(z.enum(["ggx", "multi_ggx"])),
        emission_color: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        emission_strength: orUndefined(z.number()),
        ior: orUndefined(z.number()),
        metallic: orUndefined(z.number()),
        normal: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        roughness: orUndefined(z.number()),
        sheen_roughness: orUndefined(z.number()),
        sheen_tint: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        sheen_weight: orUndefined(z.number()),
        specular_ior_level: orUndefined(z.number()),
        specular_tint: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        subsurface_anisotropy: orUndefined(z.number()),
        subsurface_ior: orUndefined(z.number()),
        subsurface_method: orUndefined(z.enum(["burley", "random_walk", "random_walk_skin"])),
        subsurface_radius: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        subsurface_scale: orUndefined(z.number()),
        subsurface_weight: orUndefined(z.number()),
        surface_mix_weight: orUndefined(z.number()),
        tangent: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        thin_film_ior: orUndefined(z.number()),
        thin_film_thickness: orUndefined(z.number()),
        transmission_weight: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<PrincipledBsdfNode>

export const rgbCurvesNodeSchema = z.strictObject({
    type: z.literal("rgb_curves"),
    inputs: z.strictObject({
        fac: orUndefined(floatInputSchema),
        value: orUndefined(colorInputSchema),
    }),
    parameters: z.strictObject({
        curves: z.array(z.tuple([z.number(), z.number(), z.number()])),
        extrapolate: orUndefined(z.boolean()),
        fac: orUndefined(z.number()),
        max_x: orUndefined(z.number()),
        min_x: orUndefined(z.number()),
        value: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<RgbCurvesNode>

export const rgbRampNodeSchema = z.strictObject({
    type: z.literal("rgb_ramp"),
    inputs: z.strictObject({
        fac: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        fac: orUndefined(z.number()),
        interpolate: orUndefined(z.boolean()),
        ramp: z.array(z.tuple([z.number(), z.number(), z.number()])),
        ramp_alpha: z.array(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<RgbRampNode>

export const rgbToBwNodeSchema = z.strictObject({
    type: z.literal("rgb_to_bw"),
    inputs: z.strictObject({
        color: orUndefined(colorInputSchema),
    }),
    parameters: z.strictObject({
        color: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<RgbToBwNode>

export const separateHsvNodeSchema = z.strictObject({
    type: z.literal("separate_hsv"),
    inputs: z.strictObject({
        color: orUndefined(colorInputSchema),
    }),
    parameters: z.strictObject({
        color: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<SeparateHsvNode>

export const separateRgbNodeSchema = z.strictObject({
    type: z.literal("separate_rgb"),
    inputs: z.strictObject({
        color: orUndefined(colorInputSchema),
    }),
    parameters: z.strictObject({
        color: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<SeparateRgbNode>

export const separateXyzNodeSchema = z.strictObject({
    type: z.literal("separate_xyz"),
    inputs: z.strictObject({
        vector: orUndefined(colorInputSchema),
    }),
    parameters: z.strictObject({
        vector: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<SeparateXyzNode>

export const subsurfaceScatteringNodeSchema = z.strictObject({
    type: z.literal("subsurface_scattering"),
    inputs: z.strictObject({
        color: orUndefined(colorInputSchema),
        normal: orUndefined(normalInputSchema),
        scale: orUndefined(floatInputSchema),
        radius: orUndefined(vectorInputSchema),
        subsurface_ior: orUndefined(floatInputSchema),
        subsurface_roughness: orUndefined(floatInputSchema),
        subsurface_anisotropy: orUndefined(floatInputSchema),
        surface_mix_weight: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        color: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        normal: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        scale: orUndefined(z.number()),
        radius: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        subsurface_ior: orUndefined(z.number()),
        subsurface_roughness: orUndefined(z.number()),
        subsurface_anisotropy: orUndefined(z.number()),
        surface_mix_weight: orUndefined(z.number()),
        method: orUndefined(z.enum(["burley", "random_walk", "random_walk_skin"])),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<SubsurfaceScatteringNode>

export const tangentNodeSchema = z.strictObject({
    type: z.literal("tangent"),
    inputs: z.strictObject({}),
    parameters: z.strictObject({
        attribute: orUndefined(z.string()),
        axis: orUndefined(z.enum(["x", "y", "z"])),
        direction_type: orUndefined(z.enum(["radial", "uv_map"])),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<TangentNode>

export const textureCoordinateNodeSchema = z.strictObject({
    type: z.literal("texture_coordinate"),
    inputs: z.strictObject({}),
    parameters: z.strictObject({
        from_dupli: orUndefined(z.boolean()),
        use_transform: orUndefined(z.boolean()),
        ob_tfm: orUndefined(transformSchema),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<TextureCoordinateNode>

export const translucentBsdfNodeSchema = z.strictObject({
    type: z.literal("translucent_bsdf"),
    inputs: z.strictObject({
        color: orUndefined(colorInputSchema),
        normal: orUndefined(normalInputSchema),
        surface_mix_weight: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        color: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        normal: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        surface_mix_weight: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<TranslucentBsdfNode>

export const transparentBsdfNodeSchema = z.strictObject({
    type: z.literal("transparent_bsdf"),
    inputs: z.strictObject({
        color: orUndefined(colorInputSchema),
        surface_mix_weight: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        color: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        surface_mix_weight: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<TransparentBsdfNode>

export const uvMapNodeSchema = z.strictObject({
    type: z.literal("uvmap"),
    inputs: z.strictObject({}),
    parameters: z.strictObject({
        attribute: orUndefined(z.string()),
        from_dupli: orUndefined(z.boolean()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<UvMapNode>

export const valueNodeSchema = z.strictObject({
    type: z.literal("value"),
    inputs: z.strictObject({}),
    parameters: z.strictObject({
        value: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<ValueNode>

export const vectorMathNodeSchema = z.strictObject({
    type: z.literal("vector_math"),
    inputs: z.strictObject({
        vector1: orUndefined(vectorInputSchema),
        vector2: orUndefined(vectorInputSchema),
        vector3: orUndefined(vectorInputSchema),
        scale: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        math_type: orUndefined(
            z.enum([
                "add",
                "subtract",
                "multiply",
                "divide",
                "cross_product",
                "project",
                "reflect",
                "refract",
                "faceforward",
                "multiply_add",
                "dot_product",
                "distance",
                "length",
                "scale",
                "normalize",
                "snap",
                "floor",
                "ceil",
                "modulo",
                "wrap",
                "fraction",
                "absolute",
                "minimum",
                "maximum",
                "sine",
                "cosine",
                "tangent",
            ]),
        ),
        vector1: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        vector2: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        vector3: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
        scale: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<VectorMathNode>

export const voronoiTextureNodeSchema = z.strictObject({
    type: z.literal("voronoi_texture"),
    inputs: z.strictObject({
        detail: orUndefined(floatInputSchema),
        exponent: orUndefined(floatInputSchema),
        lacunarity: orUndefined(floatInputSchema),
        randomness: orUndefined(floatInputSchema),
        roughness: orUndefined(floatInputSchema),
        scale: orUndefined(floatInputSchema),
        smoothness: orUndefined(floatInputSchema),
        vector: orUndefined(pointInputSchema),
        w: orUndefined(floatInputSchema),
    }),
    parameters: z.strictObject({
        detail: orUndefined(z.number()),
        dimensions: orUndefined(z.enum(["1D", "2D", "3D", "4D"])),
        exponent: orUndefined(z.number()),
        lacunarity: orUndefined(z.number()),
        metric: orUndefined(z.enum(["euclidean", "manhattan", "chebyshev", "minkowski"])),
        feature: orUndefined(z.enum(["f1", "f2", "smooth_f1", "distance_to_edge", "n_sphere_radius"])),
        randomness: orUndefined(z.number()),
        roughness: orUndefined(z.number()),
        scale: orUndefined(z.number()),
        smoothness: orUndefined(z.number()),
        use_normalize: orUndefined(z.boolean()),
        w: orUndefined(z.number()),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<VoronoiTextureNode>

export const normalNodeSchema = z.strictObject({
    type: z.literal("normal"),
    inputs: z.strictObject({
        normal: orUndefined(normalInputSchema),
    }),
    parameters: z.strictObject({
        direction: orUndefined(z.tuple([z.number(), z.number(), z.number()])),
    }),
    resources: z.strictObject({}),
}) satisfies z.ZodMiniType<NormalNode>

export const environmentTextureNodeSchema = z.strictObject({
    type: z.literal("environment_texture"),
    inputs: z.strictObject({
        vector: orUndefined(pointInputSchema),
    }),
    parameters: z.strictObject({
        alpha_type: orUndefined(z.enum(["auto", "unassociated", "associated", "channel_packed", "ignore"])),
        animated: orUndefined(z.boolean()),
        colorspace: orUndefined(z.string()),
        filename: orUndefined(z.string()),
        interpolation: orUndefined(z.enum(["closest", "linear", "cubic", "smart"])),
        projection: orUndefined(z.enum(["equirectangular", "mirrorball"])),
    }),
    resources: z.strictObject({
        image: z.strictObject({
            type: z.literal("loadImage"),
            data: z.strictObject({type: z.literal("dataObjectReference"), dataObjectId: z.number()}),
        }),
    }),
}) satisfies z.ZodMiniType<EnvironmentTextureNode>
